import { useMemo, useState } from 'react';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';

export const REPORT_DATE_RANGE_OPTIONS = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Last Month',
  'This Quarter',
  'This Year',
  'Custom Date',
] as const;

export type ReportDateRangeOption = (typeof REPORT_DATE_RANGE_OPTIONS)[number];

export function useReportDateRange(defaultRange: ReportDateRangeOption = 'This Month') {
  const [dateRange, setDateRange] = useState<ReportDateRangeOption>(defaultRange);
  const [customFrom, setCustomFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customTo, setCustomTo] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { from_date, to_date } = useMemo(() => {
    const today = new Date();
    switch (dateRange) {
      case 'Today':
        return { from_date: format(today, 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'Yesterday': {
        const yesterday = subDays(today, 1);
        return { from_date: format(yesterday, 'yyyy-MM-dd'), to_date: format(yesterday, 'yyyy-MM-dd') };
      }
      case 'This Week':
        return {
          from_date: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'Last Week': {
        const lastWeekRef = subDays(today, 7);
        return {
          from_date: format(startOfWeek(lastWeekRef, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to_date: format(endOfWeek(lastWeekRef, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      }
      case 'This Month':
        return { from_date: format(startOfMonth(today), 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'Last Month': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return {
          from_date: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          to_date: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      }
      case 'This Quarter':
        return { from_date: format(startOfQuarter(today), 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'This Year':
        return { from_date: format(startOfYear(today), 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'Custom Date':
        return { from_date: customFrom, to_date: customTo };
      default:
        return { from_date: format(startOfMonth(today), 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
    }
  }, [dateRange, customFrom, customTo]);

  return {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  };
}
